export interface CookieConsentState {
  necessary: boolean
  functional: boolean
  analytics: boolean
  marketing: boolean
}

const COOKIE_NAME = 'cookie_consent'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 // 1 year

const defaultState: CookieConsentState = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
}

export function useCookieConsent() {
  const consentCookie = useCookie<CookieConsentState | null>(COOKIE_NAME, {
    default: () => null,
    maxAge: COOKIE_MAX_AGE,
    sameSite: 'lax',
    watch: true,
  })

  const showBanner = useState('cookie-consent-show', () => !consentCookie.value)

  const state = useState<CookieConsentState>('cookie-consent-state', () => {
    return consentCookie.value ?? { ...defaultState }
  })

  const hasConsented = computed(() => consentCookie.value !== null)

  function persist(preferences: CookieConsentState) {
    const value = { ...preferences, necessary: true }
    consentCookie.value = value
    state.value = value
    showBanner.value = false
  }

  function acceptAll() {
    persist({ necessary: true, functional: true, analytics: true, marketing: true })
  }

  function declineAll() {
    persist({ ...defaultState })
  }

  function savePreferences(preferences: Partial<CookieConsentState>) {
    persist({
      necessary: true,
      functional: preferences.functional ?? false,
      analytics: preferences.analytics ?? false,
      marketing: preferences.marketing ?? false,
    })
  }

  function show() {
    showBanner.value = true
  }

  return {
    state: readonly(state),
    hasConsented,
    showBanner,
    acceptAll,
    declineAll,
    savePreferences,
    show,
  }
}
